import React from 'react';
import P from 'prop-types';
import { graphql, compose } from 'react-apollo';
import { omit, get } from 'lodash';
import { Formik, Form, Field } from 'formik';
import moment from 'moment';
import { Redirect, withRouter } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { insuranceStatuses, locales, insuranceTypes } from '@becollective/constants';

import logo from 'assets/images/becollective-logo.svg';
import { ThemeContext } from 'components/App/';
import GraphQLErrorMessage from 'components/form/GraphQLErrorMessage/';
import Button from 'components/Button/';
import UploadLogo from 'components/UploadLogo/';
import LocationPicker from 'components/LocationPicker/';
import ErrorMsg from 'components/form/ErrorMsg/';
import schema from 'formSchemas/updateGroup';
import GROUP_TYPES, { INDUSTRY_WITH_TYPES } from 'data/groupTypes';
import { GROUP_QUERY } from 'gql/queries';
import {
  CREATE_OPPORTUNITY_MUTATION,
  UPDATE_OPPORTUNITY_MUTATION,
  UPDATE_GROUP_MUTATION,
  REQUEST_PARTNERSHIP_MUTATION,
} from 'gql/mutations';
import { sanitiseGroupData } from 'gql/utils/groups';
import ensureLoggedIn from 'utils/ensureLoggedIn';
import displayLoading from 'utils/displayLoading';
import transformErrors from 'utils/transformErrors';
import { sanitizeLocation } from 'utils/location';
import config from 'config';

import 'react-datepicker/dist/react-datepicker.css';
import './UpdateGroup.scss';

const DEFAULT_APPLICATION_SETTINGS = {
  autoapprove: false,
  interview: false,
  details: [],
  questions: [],
  requirements: [],
  memberTypes: [],
  skills: [],
};

// Input:   JS Date Obj.
// Output:  null || string in format YYYY-MM-DD
const formatDate = dateObj => {
  const date = moment(dateObj).format('YYYY-MM-DD');
  return date === 'Invalid date' ? null : date;
};

const isCountryChanged = (location, newLocation) => {
  return get(location, 'country.short') !== get(newLocation, 'country.short');
};

const initInsurancesField = (groupLocation, savedInsurances) => {
  if (savedInsurances) {
    return savedInsurances;
  }

  const { countryOptions } = locales;
  const countryCode = get(groupLocation, 'country.short');
  const renderCountryCode = countryOptions[countryCode] ? countryCode : 'AU';
  const { insuranceTypes } = countryOptions[renderCountryCode];
  const insurances = insuranceTypes.map(insuranceType => ({
    insuranceType: insuranceType.type,
    vendor: '',
    policyNumber: '',
    value: '',
    expiryDate: '',
  }));

  return insurances;
};

class Location extends React.Component {
  state = {
    oldLocation: null,
  };

  render() {
    return (
      <Field>
        {({ form }) => {
          const {
            values: { location },
            setFieldTouched,
            setFieldValue,
          } = form;
          return (
            <LocationPicker
              className="UpdateGroup-location"
              location={location}
              showMap
              onChange={newLocation => {
                if (newLocation === null && location) {
                  this.setState({ oldLocation: location });
                }
                setFieldValue('location', newLocation);
                setFieldTouched('location', true);

                if (newLocation && isCountryChanged(this.state.oldLocation, newLocation)) {
                  setFieldValue('insurances', initInsurancesField(newLocation));
                }
              }}
            />
          );
        }}
      </Field>
    );
  }
}

const insuranceDetails = () => {
  return (
    <Field>
      {({ form }) => {
        const {
          values: { insurances },
          setFieldTouched,
          setFieldValue,
        } = form;
        return (
          <div className="UpdateGroup-insurance">
            {insurances.map((insurance, index) => {
              const insurancePrefix = `insurances[${index}]`;
              return (
                <React.Fragment key={insurance.insuranceType}>
                  <div className="UpdateGroup-field">
                    <label htmlFor={`${insurancePrefix}.vendor`}>{`${
                      insuranceTypes[insurance.insuranceType].label
                    }`}</label>
                    <Field
                      id={`${insurancePrefix}.vendor`}
                      type="text"
                      name={`${insurancePrefix}.vendor`}
                      placeholder="Enter insurer name"
                    />
                    <ErrorMsg name={`${insurancePrefix}.vendor`} />
                  </div>
                  <div className="UpdateGroup-field">
                    <label htmlFor={`${insurancePrefix}.policyNumber`}>Policy number</label>
                    <Field
                      id={`${insurancePrefix}.policyNumber`}
                      type="text"
                      name={`${insurancePrefix}.policyNumber`}
                      placeholder="Enter policy number"
                    />
                    <ErrorMsg name={`${insurancePrefix}.policyNumber`} />
                  </div>
                  <div className="UpdateGroup-field is-half">
                    <div>
                      <div className="UpdateGroup-field">
                        <label htmlFor={`${insurancePrefix}.value`}>Insured value</label>
                        <Field
                          id={`${insurancePrefix}.value`}
                          type="text"
                          name={`${insurancePrefix}.value`}
                          placeholder="Enter insurance value"
                        />
                        <ErrorMsg name={`${insurancePrefix}.value`} />
                      </div>
                    </div>
                    <div>
                      <div className="UpdateGroup-field">
                        <label htmlFor={`${insurancePrefix}.expiryDate`}>Expiry</label>
                        <DatePicker
                          selected={insurances[index].expiryDate}
                          onChange={date => {
                            setFieldTouched(`${insurancePrefix}.expiryDate`, true);
                            setFieldValue(`${insurancePrefix}.expiryDate`, date);
                          }}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                        />
                        <ErrorMsg name={`${insurancePrefix}.expiryDate`} />
                      </div>
                    </div>
                  </div>
                  {index < insurances.length - 1 && <hr className="UpdateGroup-hr" />}
                </React.Fragment>
              );
            })}
          </div>
        );
      }}
    </Field>
  );
};

const UpdateGroup = ({
  requestPartnershipMutation,
  updateGroupMutation,
  updateOpportunityMutation,
  createOpportunityMutation,
  match,
  data,
  location: routerLocation,
}) => {
  const { group, groupIndustries } = data.store;
  // TODO: Render Error Page
  if (!group) return null;

  const {
    id,
    draft,
    name,
    summary,
    type,
    industry,
    avatarUrl,
    location,
    insurances,
    insuranceAuspicedOrganisation,
    insuranceStatus,
  } = group;

  // We only allow updating of unpublished groups on the management-portal.
  // If a user wants to update a group after it's published, they must do so on becollective-web
  if (!draft) {
    return <Redirect to={`/group/${id}/opportunities`} />;
  }

  return (
    <ThemeContext.Consumer>
      {context => {
        const { VRCId, name: VRCName, hidePoweredByText } = context;
        const sanitisedLocation = sanitizeLocation(location);
        return (
          <div className="UpdateGroup">
            <div className="UpdateGroup-main">
              <h2 className="UpdateGroup-heading">Finalise your group</h2>
              {!hidePoweredByText && (
                <div className="UpdateGroup-bc">
                  Powered by <img className="UpdateGroup-bcLogo" src={logo} alt="Be Collective" />
                </div>
              )}
              <Formik
                validationSchema={schema}
                initialValues={{
                  avatarUrl,
                  location: sanitisedLocation,
                  // name, summary, type, industry were fields that were:
                  // 1. Filled in by user in <Register /> and saved in localStorage
                  // 2. Sent to server in <VerifyEmail /> (unless user's localStorage was cleared between the
                  //    time they registered and verified their email).
                  name,
                  summary: summary || '',
                  type,
                  industry,
                  // Insurance
                  insuranceAuspicedOrganisation,
                  insuranceStatus: insuranceStatus || insuranceStatuses.UNINSURED.value,
                  insurances: initInsurancesField(sanitisedLocation, insurances),
                }}
                onSubmit={(values, { setErrors, setSubmitting }) => {
                  const inputData = sanitiseGroupData({
                    ...omit(values, ['avatarUrl', 'location']),
                    location: sanitizeLocation(values.location),
                    applicationSettings: DEFAULT_APPLICATION_SETTINGS,
                    id,
                    draft: false,
                  });

                  if (inputData.insuranceStatus === insuranceStatuses.INSURED.value) {
                    inputData.insurances = values.insurances.map(insurance => ({
                      ...insurance,
                      expiryDate: formatDate(insurance.expiryDate),
                    }));
                  }

                  updateGroupMutation({
                    variables: {
                      input: inputData,
                    },
                  })
                    .then(({ data }) => {
                      return requestPartnershipMutation({
                        variables: {
                          input: {
                            requesterId: id,
                            requesteeId: VRCId,
                            reason: 'Automated partnership request from website',
                          },
                        },
                      });
                    })
                    .then(({ data }) => {
                      return createOpportunityMutation({
                        variables: {
                          input: {
                            groupId: id,
                          },
                        },
                      });
                    })
                    .then(({ data }) => {
                      const { id } = data.createOpportunityV2.newOpportunity;

                      return updateOpportunityMutation({
                        variables: {
                          input: {
                            id,
                            sharedOtherGroupIds: [VRCId],
                          },
                        },
                      });
                    })
                    .then(({ data }) => {
                      const { id } = data.editOpportunityV2.opportunity;

                      const referrer = `management-portal${routerLocation.pathname}`;

                      window.location.href = `${config.WEB_URL}/opportunities/${id}/wizard?referrer=${referrer}&ignoreRegionGuess=true`;
                    })
                    .catch(err => {
                      setErrors(transformErrors(err));
                      setSubmitting(false);
                    });
                }}
              >
                {({ isSubmitting, values, errors, setFieldTouched, setFieldValue }) => {
                  const countryCode = get(values.location, 'country.short');
                  return (
                    <Form>
                      {!name && (
                        <div className="UpdateGroup-field">
                          <label htmlFor="name">Group name</label>
                          <Field id="name" name="name" placeholder="Group name" />
                          <ErrorMsg name="name" />
                        </div>
                      )}
                      {!summary && (
                        <div className="UpdateGroup-field">
                          <label htmlFor="summary">Group description</label>
                          <Field
                            id="summary"
                            name="summary"
                            component="textarea"
                            placeholder="Tell everyone about your group"
                          />
                          <ErrorMsg name="summary" />
                        </div>
                      )}
                      {!type && (
                        <div className="Register-field is-half">
                          <div>
                            <div className="Register-field">
                              <label htmlFor="type">Group type</label>
                              <Field id="type" name="type" component="select">
                                <option value="">Select Group Type</option>
                                {GROUP_TYPES.map(({ name, value }) => {
                                  return (
                                    <option key={value} value={value}>
                                      {name}
                                    </option>
                                  );
                                })}
                              </Field>
                            </div>
                            <ErrorMsg name="type" />
                          </div>
                          {INDUSTRY_WITH_TYPES.includes(values.type) && (
                            <div>
                              <div className="Register-field">
                                <label htmlFor="industry">Industry / Sector *</label>
                                <Field id="industry" name="industry" component="select">
                                  <option value="">Select Industry/Sector</option>
                                  {groupIndustries.map(industry => {
                                    return (
                                      <option key={industry} value={industry}>
                                        {industry}
                                      </option>
                                    );
                                  })}
                                </Field>
                              </div>
                              <ErrorMsg name="industry" />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="UpdateGroup-field">
                        <label htmlFor="avatarUrl">Group logo</label>
                        <UploadLogo
                          model="Groups"
                          id={id}
                          type="avatar"
                          name="avatarUrl"
                          imgUrl={values.avatarUrl}
                          onChange={avatarUrl => {
                            setFieldTouched('avatarUrl', true);
                            setFieldValue('avatarUrl', avatarUrl);
                          }}
                        />
                      </div>

                      <div className="UpdateGroup-field">
                        <label htmlFor="location">Location</label>
                        <Location />
                        <ErrorMsg
                          name="location"
                          render={() => {
                            return <div className="ErrorMsg">You must provide a physical location for the group</div>;
                          }}
                        />
                      </div>

                      <h3 className="UpdateGroup-groupHeading">Insurance</h3>
                      <div className="UpdateGroup-insuranceDesc">
                        Volunteers and other groups (organisations) on the system will{' '}
                        <i>
                          <b>only</b>
                        </i>{' '}
                        be able to see whether or not you have insurance. The insurance details (policy name and number)
                        are confidential and not shared with anyone.
                        <br />
                        <br />
                        You may add insurance details at a later time by selecting “we don’t have insurance”. However,
                        your volunteer opportunities will not be listed on the {VRCName} website until you do so.
                      </div>

                      <div className="UpdateGroup-radio">
                        <label>
                          <input
                            type="radio"
                            name="insuranceStatus"
                            value={insuranceStatuses.INSURED.value}
                            defaultChecked={values.insuranceStatus === insuranceStatuses.INSURED.value}
                            onChange={e => {
                              setFieldTouched('insuranceStatus', true);
                              setFieldValue('insuranceStatus', insuranceStatuses.INSURED.value);
                              setFieldTouched('location', true);
                            }}
                          />
                          We have insurance
                        </label>
                      </div>
                      <div className="UpdateGroup-radio">
                        <label>
                          <input
                            type="radio"
                            name="insuranceStatus"
                            value={insuranceStatuses.UNINSURED.value}
                            defaultChecked={values.insuranceStatus === insuranceStatuses.UNINSURED.value}
                            onChange={e => {
                              setFieldTouched('insuranceStatus', true);
                              setFieldValue('insuranceStatus', insuranceStatuses.UNINSURED.value);
                              setFieldTouched('location', true);
                            }}
                          />
                          We don't have insurance
                        </label>
                      </div>
                      <div className="UpdateGroup-radio">
                        <label>
                          <input
                            type="radio"
                            name="insuranceStatus"
                            value={insuranceStatuses.AUSPICED.value}
                            defaultChecked={values.insuranceStatus === insuranceStatuses.AUSPICED.value}
                            onChange={e => {
                              setFieldTouched('insuranceStatus', true);
                              setFieldValue('insuranceStatus', insuranceStatuses.AUSPICED.value);
                              setFieldTouched('location', true);
                            }}
                          />
                          We are auspiced by another organisation
                        </label>
                      </div>

                      {values.insuranceStatus === insuranceStatuses.INSURED.value && countryCode && insuranceDetails()}

                      {values.insuranceStatus === insuranceStatuses.INSURED.value && !countryCode && (
                        <ErrorMsg
                          name="location"
                          render={() => {
                            return <div className="ErrorMsg">You must provide a physical location for the group</div>;
                          }}
                        />
                      )}

                      {values.insuranceStatus === insuranceStatuses.AUSPICED.value && (
                        <div className="UpdateGroup-insurance">
                          <div className="UpdateGroup-field">
                            <label htmlFor="insuranceAuspicedOrganisation">Organisation name</label>
                            <Field
                              id="insuranceAuspicedOrganisation"
                              type="text"
                              name="insuranceAuspicedOrganisation"
                              placeholder="Enter auspice organisation name"
                            />
                          </div>
                          <ErrorMsg name="insuranceAuspicedOrganisation" />
                        </div>
                      )}

                      <GraphQLErrorMessage errors={errors} />
                      <Button className="Login-submit" type="submit">
                        Finalise group
                      </Button>
                    </Form>
                  );
                }}
              </Formik>
            </div>
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};

UpdateGroup.propTypes = {
  match: P.shape({
    params: P.shape({
      id: P.string,
    }).isRequired,
  }).isRequired,
  data: P.shape({
    store: P.shape({
      group: P.shape({
        id: P.string.isRequired,
        avatarUrl: P.string,
        location: P.oneOfType([
          P.string,
          P.shape({
            address: P.string,
            locality: P.object,
            region: P.object,
            country: P.object,
          }),
        ]),
        insurances: P.arrayOf(
          P.shape({
            insuranceType: P.string,
            vendor: P.string,
            expiryDate: P.string,
            value: P.string,
          }),
        ),
      }).isRequired,
    }).isRequired,
  }).isRequired,
};

export default compose(
  graphql(GROUP_QUERY, {
    options: ({ match }) => ({
      variables: {
        id: match.params.id || null,
      },
      fetchPolicy: 'network-only',
    }),
  }),
  graphql(UPDATE_GROUP_MUTATION, { name: 'updateGroupMutation' }),
  graphql(REQUEST_PARTNERSHIP_MUTATION, { name: 'requestPartnershipMutation' }),
  graphql(CREATE_OPPORTUNITY_MUTATION, { name: 'createOpportunityMutation' }),
  graphql(UPDATE_OPPORTUNITY_MUTATION, { name: 'updateOpportunityMutation' }),
  ensureLoggedIn,
  withRouter,
  displayLoading(),
)(UpdateGroup);
